import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ClipboardList } from "lucide-react";
import { Shell } from "@/components/control-tower/Layout";
import { PerspectiveHero } from "@/components/control-tower/PerspectiveHero";
import { PerspectiveOverview } from "@/components/control-tower/perspectives/PerspectiveOverview";
import { KbCycleRibbon } from "@/components/control-tower/perspectives/KbCycleRibbon";
import { LensQuietCaption } from "@/components/control-tower/perspectives/LensTopicSection";
import { useScopedFindings } from "@/lib/api/useScopedFindings";
import { ConfigStatusTabs } from "@/components/configuration/ConfigStatusTabs";
import { CapabilityList } from "@/components/configuration/CapabilityList";
import { RakenneWorkspace } from "@/components/configuration/RakenneWorkspace";
import { ServicesSection } from "@/components/configuration/ServicesSection";
import { ConfigDataIntakeSection } from "@/components/control-tower/perspectives/building/ConfigDataIntakeSection";
import { ConfigSignalMappingSection } from "@/components/control-tower/perspectives/building/ConfigSignalMappingSection";
import { SectionPanel } from "@/components/ui/section-panel";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { useConfigSetupLens } from "@/lib/useConfigSetupLens";
import { isConfigurator, isOperator } from "@/lib/auth";
import type { OverviewCopy } from "@/lib/overview-lens";
import type { ConfigTabKey } from "@/lib/useBuildingConfigStatus";

// Konfiguraatio's fill of the shared portfolio-scope overview grammar (docs/spec/concepts.md
// §2.4 → "Konfiguraatio portfolio-scope IA") — the setup-specific copy every other lens supplies
// its own version of.
const CONFIG_OVERVIEW_COPY: OverviewCopy = {
  situationCardSubtitle: (count) =>
    count === 1
      ? "1 kiinteistö tarvitsee vielä"
      : `${count} kiinteistöä tarvitsee vielä`,
  situationCardAllDoneLabel: "Kaikki valmiina",
  emptyScopeHint: "Ei kiinteistöjä valitulla rajauksella",
  tileCompleteLabel: "Valmis",
  mapTitle: "Kiinteistökannan setup-kartta",
  mapSubtitle:
    "Missä on eniten setup-työtä jäljellä — pallon koko = kiinteistöjä joilla setup on kesken",
  mapLegendOkLabel: "Valmis",
  mapLegendWarnLabel: "Tarvitsee huomiota",
};

export const Route = createFileRoute("/configuration")({
  component: ConfigurationPage,
});

// Konfiguraatiotarkastaja's Finding.category — the same filter shape every judgment lens's
// LensQuietCaption uses (docs/spec/concepts.md → "Configuration completeness"). Exported so
// configuration.test.tsx can render LensQuietCaption with the exact filter this page passes it,
// rather than a hand-copied `{ category: "configuration" }` that could silently drift.
// eslint-disable-next-line react-refresh/only-export-components -- exported for configuration.test.tsx
export const CONFIGURATION_FINDINGS_FILTER = {
  category: "configuration" as const,
};

// Konfiguraatio only shows the quiet Havainnot caption at building scope — portfolio scope
// renders ConfigurationPortfolio instead, which never mounts it. This repo has no DOM-render
// test infra for hook-heavy pages (useInventoryFilter/useBuildings need the full app's router +
// context tree to render), so the scope gate itself is pulled out as a pure, hook-free function
// and driven by it below — the caption's own markup (count, links) gets a direct render test
// via LensQuietCaption instead, which needs no such context (configuration.test.tsx).
// eslint-disable-next-line react-refresh/only-export-components -- exported for configuration.test.tsx
export function showsConfigurationCaption(
  buildingId: string | null,
): buildingId is string {
  return buildingId !== null;
}

// Konfiguraatio perspective — scope-adaptive: one building's configuration (tabbed) vs. the
// portfolio coverage overview. The four status cards double as the tab bar (validity at a glance,
// no live values); each opens its own view. Writes are RBAC-gated inside each view.
function ConfigurationPage() {
  const { buildingId } = useInventoryFilter();
  const [activeTab, setActiveTab] = useState<ConfigTabKey>("rakenne");
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);
  const operator = isOperator();
  const configurator = isConfigurator();

  return (
    <Shell
      title="Konfiguraatio"
      subtitle="Rakenne · signaalit · odotettu toiminta"
    >
      <div className="space-y-5">
        <PerspectiveHero perspectiveId="configuration" />
        {showsConfigurationCaption(buildingId) ? (
          <>
            <ConfigurationFindingsCaption />
            <KbCycleRibbon buildingId={buildingId} />
            <ConfigStatusTabs
              buildingId={buildingId}
              activeTab={activeTab}
              onSelect={setActiveTab}
              refreshKey={refreshKey}
            />

            {activeTab === "rakenne" && (
              <RakenneWorkspace
                buildingId={buildingId}
                operator={operator}
                configurator={configurator}
                onChanged={bump}
              />
            )}

            {activeTab === "kyvykkyydet" && (
              <CapabilityList
                buildingId={buildingId}
                operator={operator}
                configurator={configurator}
                onChanged={bump}
                onGoToSignaalit={() => setActiveTab("signaalit")}
              />
            )}

            {activeTab === "signaalit" && (
              <div className="space-y-5">
                {operator && (
                  <ConfigDataIntakeSection
                    buildingId={buildingId}
                    onChanged={bump}
                  />
                )}
                <ConfigSignalMappingSection
                  buildingId={buildingId}
                  operator={operator}
                  refreshKey={refreshKey}
                  onChanged={bump}
                />
              </div>
            )}

            {activeTab === "palvelut" && (
              <SectionPanel
                icon={ClipboardList}
                title="Ulkoiset palvelut"
                subtitle="Aktivointi avaa optimoinnin potentiaalia — sää, hinta, CO₂, kuormanjousto"
              >
                <ServicesSection buildingId={buildingId} operator={operator} />
              </SectionPanel>
            )}
          </>
        ) : (
          <ConfigurationPortfolio />
        )}
      </div>
    </Shell>
  );
}

// Building scope only — Konfiguraatio keeps the quiet Toiminto caption the four judgment /
// optimization pages no longer render (docs/spec/concepts.md § Six perspectives). It is not a
// LensTopicSection consumer (bespoke tabbed layout), so it fetches its own scoped findings — a
// separate component (not a hook call inside ConfigurationPage) so the fetch only fires while
// this is actually mounted, never wasted at portfolio scope.
function ConfigurationFindingsCaption() {
  const { findings } = useScopedFindings(CONFIGURATION_FINDINGS_FILTER);
  return (
    <LensQuietCaption
      findings={findings}
      filter={CONFIGURATION_FINDINGS_FILTER}
    />
  );
}

// Konfiguraatio at Kiinteistökanta / Kiinteistösalkku — the non-building branch. Mounts the
// shared portfolio-scope overview grammar (hero, filters, situation cards, building tiles, map)
// filled with setup facts instead of energy/€ (docs/spec/concepts.md §2.4 →
// "Konfiguraatio portfolio-scope IA"). No nav-tile row: Tietopohja lives in Info, Tapahtumaloki
// is a left-nav Toiminto — both destinations already exist elsewhere in the shell (§2.3.1); a
// duplicate pair of destination tiles here would just be a second way to reach them.
function ConfigurationPortfolio() {
  const lens = useConfigSetupLens();

  return <PerspectiveOverview state={lens} copy={CONFIG_OVERVIEW_COPY} />;
}
